"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "beverage-cooler-size-calculator",
  "energy-cost-calculator",
  "freezer-btu-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Display Cooler Calculator",
  category: "Size & Capacity" as const,
  mediumDescription:
    "Size a commercial display cooler by interior cubic feet. We compute BTU load (200 BTU/cu ft baseline, +20% for glass doors), shelf count, product capacity, and annual energy cost.",
  faqs: [
    { question: "Why do glass doors need more BTU?", answer: "Glass conducts and radiates heat into the cabinet faster than insulated panels. Industry sizing adds roughly 20% BTU load for glass-door display coolers to compensate." },
    { question: "What temperature should a display cooler hold?", answer: "Refrigerated display coolers should hold 35–38°F (1.6–3.3°C) per FDA Food Code. Frozen displays should hold 0°F (-18°C) or lower." },
    { question: "How many cans fit per cubic foot?", answer: "Standard 12 oz cans fit ~32 per cubic foot at typical merchandiser spacing. Bottles take ~30% more volume than cans." },
    { question: "Do display coolers cost more to run than home fridges?", answer: "Yes. Glass doors, frequent opening, and bright lighting push commercial display coolers to roughly 3–5 kWh per cubic foot per day, much higher than a residential fridge." },
    { question: "How often should condenser coils be cleaned?", answer: "Monthly cleaning is the manufacturer norm. Dust buildup on the condenser raises compressor temperature and can cut efficiency by 10–15%." },
  ],
  relatedTools: ["beverage-cooler-size-calculator", "energy-cost-calculator", "freezer-btu-calculator"],
}

export function DisplayCoolerCalculatorPageClient() {
  const [width, setWidth] = useState("48")
  const [height, setHeight] = useState("78")
  const [depth, setDepth] = useState("30")
  const [productType, setProductType] = useState("beverages")
  const [doorType, setDoorType] = useState("glass")

  const result = useMemo(() => {
    // Interior inches. The floor is one shelf bay — a cabinet under a foot in
    // any direction holds no product facing and is not a display cooler; the
    // ceilings are the largest single cabinets built (a five-door reach-in, an
    // 8 ft tall merchandiser, a pass-through depth).
    const w = readQuantity(width, { min: 12, max: 120 })
    const h = readQuantity(height, { min: 12, max: 96 })
    const d = readQuantity(depth, { min: 12, max: 60 })
    if (w === null || h === null || d === null) return null
    const cubicFeet = (w * h * d) / 1728
    let btuPerCuFt = 200
    if (doorType === "glass") btuPerCuFt *= 1.2
    const btuRequired = Math.ceil(cubicFeet * btuPerCuFt)
    const shelfCount = Math.max(0, Math.floor(h / 14) - 1)
    const kwhPerYear = cubicFeet * 4 * 365
    const energyCost = kwhPerYear * 0.13
    let primaryUnit = ""
    let primaryQty = 0
    if (productType === "beverages") { primaryUnit = "12 oz cans"; primaryQty = Math.floor(cubicFeet * 32) }
    else if (productType === "dairy") { primaryUnit = "milk gallons"; primaryQty = Math.floor(cubicFeet * 8) }
    else if (productType === "grab-and-go") { primaryUnit = "sandwiches"; primaryQty = Math.floor(cubicFeet * 20) }
    else { primaryUnit = "ice cream pints"; primaryQty = Math.floor(cubicFeet * 24) }
    return {
      cubicFeet: parseFloat(cubicFeet.toFixed(1)),
      btuRequired,
      shelfCount,
      energyCost: parseFloat(energyCost.toFixed(0)),
      primaryUnit,
      primaryQty,
    }
  }, [width, height, depth, productType, doorType])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Spec a display cooler that"
      heroTitleItalic="actually"
      heroTitlePost="holds temp."
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "ASHRAE / FDA Food Code"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "200", label: "BTU / cu ft", note: "Refrigeration load" },
            { value: "+20%", label: "Glass door", note: "BTU adder" },
            { value: "32", label: "Cans / cu ft", note: "Merchandiser fit" },
            { value: "35–38°F", label: "FDA hold temp", note: "Refrigerated" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "display-cooler-calculator")}
      cta={{
        title: "From spec to shelf — find a cooler in your range.",
        body: "Browse counter-depth refrigerators when you need merchandiser-style fit and easy front access.",
        buttonLabel: "Browse counter-depth fridges",
        buttonHref: "/counter-depth-refrigerators",
      }}
    >
      {/* CALCULATOR */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Five inputs.
                <br />
                Real BTU load.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Interior dimensions, door type, and product mix drive BTU and energy cost.
              </p>

              <div className="mt-7 space-y-5">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">W (in)</label>
                    <input type="number" min={12} max={120} step="0.5" value={width} onChange={(e) => setWidth(e.target.value)} className="tool-input-clean w-full px-3 py-3 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">H (in)</label>
                    <input type="number" min={12} max={96} step="0.5" value={height} onChange={(e) => setHeight(e.target.value)} className="tool-input-clean w-full px-3 py-3 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">D (in)</label>
                    <input type="number" min={12} max={60} step="0.5" value={depth} onChange={(e) => setDepth(e.target.value)} className="tool-input-clean w-full px-3 py-3 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">Door type</label>
                  <select value={doorType} onChange={(e) => setDoorType(e.target.value)} className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white">
                    <option value="glass">Glass (merchandiser)</option>
                    <option value="solid">Solid (insulated)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">Primary product</label>
                  <select value={productType} onChange={(e) => setProductType(e.target.value)} className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white">
                    <option value="beverages">Beverages</option>
                    <option value="dairy">Dairy</option>
                    <option value="grab-and-go">Grab & go</option>
                    <option value="frozen">Frozen</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Product mix drives unit-capacity estimate.
                  </div>
                </div>
                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">BTU load required</div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.btuRequired ?? 0}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">BTU/hr</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result ? `${result.cubicFeet} cu ft interior · ~${result.primaryQty} ${result.primaryUnit}` : "Enter dimensions to see load."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Shelves</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.shelfCount ?? "—"}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Annual cost</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{`$${result?.energyCost ?? "—"}`}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FIRST CAROUSEL */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Pick the cabinet"
          title="Counter-depth fridges with merchandiser-style fit"
          description="Front-access depth that sits flush with countertops — the residential analog of a display cooler."
          collectionType="counter-depth"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* METHODOLOGY */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                ASHRAE math. <em className="italic text-[#ff9951]">FDA</em> temps.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                BTU baseline from ASHRAE refrigeration load tables; hold-temperature targets from FDA Food Code 2022.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Cubic feet from W×H×D", d: "(W × H × D) ÷ 1728. Convert inches to cubic feet of interior volume." },
                { n: "02", t: "BTU = cu ft × 200 (× 1.2 glass)", d: "Refrigeration load is 200 BTU/hr per cu ft baseline. Glass doors add 20% to handle radiant heat gain." },
                { n: "03", t: "Energy = 4 kWh/cu ft/day", d: "Display coolers consume ~4 kWh per cubic foot per day. Multiply by 365 × your rate for annual cost." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* SECOND CAROUSEL */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Bottom-freezer fridges for grab-and-go zones"
          description="Eye-level fresh-food access keeps high-turnover inventory in the easiest zone to merchandise."
          collectionType="bottom-freezer"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
